import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { VehiclePermissionAffectedBooking } from "../types/VehiclePermissions.types";
import { Colleague } from "../../../features/Connections/types/Colleague.type";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { formatBookingRange, formatBookingUserName } from "./AffectedBookingsDialog.helpers";

export type AffectedBookingsConfirmDialogProps = {
  open: boolean;
  affectedBookings: VehiclePermissionAffectedBooking[];
  vehicleName?: string;
  /** Optional user list to resolve userBookedFor to display name (e.g. from useRemoteFetchUserInfoForAllUsers) */
  allUsers?: Colleague[] | null;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Shows affected bookings when changing vehicle permissions and requires
 * user confirmation before saving (PATCH vehicle with RemoteFleetPermissions).
 */
export function AffectedBookingsConfirmDialog({
  open,
  affectedBookings,
  vehicleName,
  allUsers,
  onConfirm,
  onCancel
}: Readonly<AffectedBookingsConfirmDialogProps>) {
  const { t } = useTranslation();
  const { dateFormat, timeFormat } = useTimeFormat();
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t("Affected bookings")}
        {vehicleName ? ` – ${vehicleName}` : ""}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("_changingPermissionsAffectsBookings")}
        </Typography>
        <Table size="small" sx={{ "& td, & th": { borderBottom: 1, borderColor: "divider" } }}>
          <TableHead>
            <TableRow>
              <TableCell component="th" variant="head">
                {t("Name")}
              </TableCell>
              <TableCell component="th" variant="head">
                {t("Date and Time")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {affectedBookings.map((b, index) => (
              <TableRow key={b.id ?? index}>
                <TableCell>{formatBookingUserName(b, t, allUsers)}</TableCell>
                <TableCell>{formatBookingRange(b, t, dateTimeFormat)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onCancel}
          color="secondary"
          data-testid="affected-bookings-confirm-dialog-cancel-btn"
        >
          {t("Cancel")}
        </Button>
        <Button onClick={onConfirm} data-testid="affected-bookings-confirm-dialog-confirm-btn">
          {t("Confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
